import React, { ReactNode } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Divider,
} from '@mui/material';

export interface ConfirmationDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  confirmColor?: 'error' | 'primary' | 'secondary' | 'success' | 'warning';
  children?: ReactNode;
  loading?: boolean;
}

const ConfirmationDialog: React.FC<ConfirmationDialogProps> = ({
  open,
  onClose,
  onConfirm,
  title,
  message,
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  confirmColor = 'primary',
  children,
  loading = false,
}) => {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '16px',
          boxShadow: '0 20px 60px rgba(0,0,0,0.12)',
          overflow: 'hidden',
        },
      }}
    >
      {/* Header strip */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #206347 0%, #2d7a58 100%)',
          px: 3,
          py: 2.5,
        }}
      >
        <Typography
          variant="h6"
          sx={{
            color: '#fff',
            fontWeight: 700,
            fontSize: '1.05rem',
            letterSpacing: '-0.01em',
          }}
        >
          {title}
        </Typography>
      </Box>

      <DialogContent sx={{ px: 3, pt: 2.5, pb: 1 }}>
        <Typography
          variant="body2"
          sx={{
            color: '#4B5563', // gray-600
            lineHeight: 1.65,
            fontSize: '0.875rem',
          }}
        >
          {message}
        </Typography>

        {children && <Box sx={{ mt: 2 }}>{children}</Box>}
      </DialogContent>

      <Divider sx={{ mx: 3, borderColor: 'rgba(0,0,0,0.06)' }} />

      <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
        <Button
          onClick={onClose}
          disabled={loading}
          sx={{
            color: '#374151',
            fontWeight: 500,
            borderRadius: '8px',
            px: 2.5,
            py: 1,
            textTransform: 'none',
            '&:hover': { bgcolor: 'rgba(0,0,0,0.05)' },
          }}
        >
          {cancelText}
        </Button>

        <Button
          onClick={onConfirm}
          disabled={loading}
          sx={{
            bgcolor: '#206347',
            color: '#fff',
            fontWeight: 600,
            borderRadius: '8px',
            px: 3,
            py: 1,
            textTransform: 'none',
            boxShadow: '0 2px 8px rgba(32,99,71,0.35)',
            '&:hover': {
              bgcolor: '#185238',
              boxShadow: '0 4px 12px rgba(32,99,71,0.4)',
            },
            '&:disabled': {
              bgcolor: 'rgba(32,99,71,0.4)',
              color: '#fff',
            },
          }}
        >
          {loading ? 'Processing...' : confirmText}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ConfirmationDialog;
